import {Inject, Injectable} from '@angular/core';
import {Observable, ReplaySubject} from 'rxjs';
import {CountedAndPaginated} from '../interface/response/counted-and-paginated';
import {HttpClient} from '@angular/common/http';
import {FilterService} from './filter.service';
import {DOCUMENT} from '@angular/common';
import {first} from 'rxjs/operators';
import {Uuid} from '../interface/response/uuid';
import {CommissionRedirection} from '../interface/commission-redirection';
import {CommissionRedirectionOfwcaRelations} from '../interface/commission-redirection-ofwca-relations';
import {CommissionRedirectionImport} from '../interface/commission-redirection-import';

@Injectable({
  providedIn: 'root'
})
export class CommissionRedirectionService {
  public readonly commissionRedirectionListSubject: ReplaySubject<CountedAndPaginated<CommissionRedirection>>
    = new ReplaySubject<CountedAndPaginated<CommissionRedirection>>(1);
  public readonly importListSubject: ReplaySubject<CountedAndPaginated<CommissionRedirectionImport>>
    = new ReplaySubject<CountedAndPaginated<CommissionRedirectionImport>>(1);

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  public loadList(
    page: number,
    pageSize: number,
    filters: object,
  ): void {
    this.httpClient.get(`/rest/crm/commission-redirection?page=${page + 1}&pageSize=${pageSize}&`
      + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<CommissionRedirection>) => {
        this.commissionRedirectionListSubject.next(result);
      });
  }

  public loadImportList(
    page: number,
    pageSize: number,
    filters: object,
  ): void {
    this.httpClient.get(`/rest/crm/commission-redirection-import?page=${page + 1}&pageSize=${pageSize}&`
      + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<CommissionRedirectionImport>) => {
        this.importListSubject.next(result);
      });
  }

  public downloadReport(filters: any): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/commission-redirection/method/report?`
      + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public downloadImportsReport(filters: any): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/commission-redirection-import/method/report?`
      + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public get(uuid: string): Observable<CommissionRedirection> {
    return this.httpClient.get(`/rest/crm/commission-redirection/${uuid}`)
      .pipe(first()) as Observable<CommissionRedirection>;
  }

  public delete(uuid: string): Observable<any> {
    return this.httpClient.delete(`/rest/crm/commission-redirection/${uuid}`)
      .pipe(first());
  }

  public sendForm(formData: any, uuid: string): Observable<Uuid> {
    if (uuid) {
      return this.httpClient.put(`/rest/crm/commission-redirection/${uuid}`, formData)
        .pipe(first()) as Observable<Uuid>;
    } else {
      return this.httpClient.post(`/rest/crm/commission-redirection`, formData)
        .pipe(first()) as Observable<Uuid>;
    }
  }

  public getOfwcaRelations(companyId: number, ofwcaId: string): Observable<CommissionRedirectionOfwcaRelations> {
    return this.httpClient.get(`/rest/crm/commission-redirection/method/get-ofwca-relations/${ofwcaId}/company/${companyId}`)
      .pipe(first()) as Observable<CommissionRedirectionOfwcaRelations>;
  }
  public getOfwcaRelationsContract(companyId: number, ofwcaId: string, insuranceCompanyId: string): Observable<CommissionRedirectionOfwcaRelations> {
    return this.httpClient.get(`/rest/crm/commission-redirection/method/get-ofwca-relations/${ofwcaId}/company/${companyId}/insurance-company/${insuranceCompanyId}`)
      .pipe(first()) as Observable<CommissionRedirectionOfwcaRelations>;
  }

  public import(formData: any): Observable<any> {
    return this.httpClient.post(`/rest/crm/commission-redirection-import`, formData)
      .pipe(first());
  }

  public downloadImportFile(importId: string): void {
    this.document.location.href = `/rest/crm/commission-redirection-import/${importId}/downloadFile`;
  }

  public updateRatePaid(formData: any, uuid: string): Observable<any> {
    return this.httpClient.put(`/rest/crm/commission-redirection/${uuid}/method/update-rate-paid`, formData)
      .pipe(first());
  }

  public deleteImport(uuid: string): Observable<any> {
    return this.httpClient.delete(`/rest/crm/commission-redirection-import/${uuid}`)
      .pipe(first());
  }
}
